/**
 * Settings tab — manage workspace-scoped custom diagram types.
 *
 * Permissions (mirrored from the worker handler):
 * - Any workspace member can create a custom type.
 * - The creator can edit / delete their own types.
 * - A workspace-admin can flip the "centraal beheerd" flag; once on,
 *   only admins can edit / delete the type.
 *
 * Open Group viewpoints (the static list in archimate-pack.json) are
 * read-only: they don't appear in this settings tab. Editing them is a
 * future feature for admins (handled at the registry-pack level, not
 * via this UI).
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type CustomViewKindDTO } from "../api/client";
import { getRegistry, resolveBilingual } from "../registry";
import { isAdminOfWorkspace, useSession } from "../state/session";
import { useWorkspaceData } from "../state/workspace";
import { ld, pill } from "./ledgerUi";

export function DiagramTypesSettings() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const me = useSession((s) => s.me);
  const workspaceId = useSession((s) => s.workspaceId);
  const customViewKinds = useWorkspaceData((s) => s.customViewKinds);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const myEmail = me?.email ?? "";
  const myRole = useMemo(
    () => (isAdminOfWorkspace(me, workspaceId) ? "admin" : "member"),
    [me, workspaceId],
  );

  if (!workspaceId) {
    return <p className={ld.muted}>{t("settings.common.noWorkspace")}</p>;
  }

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <p className={`${ld.viewIntro} mt-0 max-w-[60ch]`}>
          {t("settings.diagramTypes.description")}
        </p>
        <button
          type="button"
          onClick={() => {
            setCreating(true);
            setEditingId(null);
            setError(null);
          }}
          className={ld.primary}
        >
          {t("settings.diagramTypes.newButton")}
        </button>
      </header>

      {error && (
        <p role="alert" className={ld.error}>
          {error}
        </p>
      )}

      {creating && (
        <Editor
          mode="create"
          workspaceId={workspaceId}
          onClose={() => setCreating(false)}
          onError={setError}
        />
      )}

      {customViewKinds.length === 0 && !creating ? (
        <p className={`${ld.well} text-(--ld-muted) text-sm`}>
          {t("settings.diagramTypes.emptyHintBefore")}
          <span className="font-medium text-(--ld-text)">
            {t("settings.diagramTypes.emptyHintCta")}
          </span>
          {t("settings.diagramTypes.emptyHintAfter")}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {customViewKinds.map((c) => {
            const isEditing = editingId === c.id;
            if (isEditing) {
              return (
                <li key={c.id}>
                  <Editor
                    mode="edit"
                    workspaceId={workspaceId}
                    initial={c}
                    role={myRole}
                    onClose={() => setEditingId(null)}
                    onError={setError}
                  />
                </li>
              );
            }
            const editable = canEdit(c, myEmail, myRole);
            return (
              <li key={c.id} className={ld.panel}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate font-medium text-sm">
                        {resolveBilingual(c.name, lang) || c.id}
                      </h3>
                      {c.centrallyManaged && (
                        <span className={pill.notice}>
                          {t("settings.diagramTypes.centrallyManagedBadge")}
                        </span>
                      )}
                    </div>
                    {c.description && (
                      <p className="mt-1 text-pretty text-(--ld-muted) text-[13px] leading-5">
                        {resolveBilingual(c.description, lang)}
                      </p>
                    )}
                    <p className="mt-2 text-(--ld-muted) text-xs leading-5">
                      {t("settings.diagramTypes.conceptTypesCount", {
                        count: c.includedTypes.length,
                      })}
                      {c.includedRelationTypes && (
                        <>
                          {", "}
                          {t("settings.diagramTypes.relationTypesCount", {
                            count: c.includedRelationTypes.length,
                          })}
                        </>
                      )}
                      {" · "}
                      <span className="font-mono">{c.id}</span>
                      {t("settings.diagramTypes.creatorLabel")}
                      {c.creatorEmail}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-4">
                    <button
                      type="button"
                      disabled={!editable}
                      onClick={async () => {
                        setError(null);
                        if (
                          !window.confirm(
                            t("settings.diagramTypes.deleteConfirm", {
                              name: resolveBilingual(c.name, lang) || c.id,
                            }),
                          )
                        )
                          return;
                        try {
                          await useWorkspaceData.getState().deleteCustomViewKind(workspaceId, c.id);
                        } catch (err) {
                          setError(explain(err));
                        }
                      }}
                      className={ld.textDanger}
                    >
                      {t("settings.common.delete")}
                    </button>
                    <button
                      type="button"
                      disabled={!editable}
                      onClick={() => {
                        setEditingId(c.id);
                        setCreating(false);
                        setError(null);
                      }}
                      className={ld.secondarySm}
                      title={
                        editable
                          ? ""
                          : c.centrallyManaged
                            ? t("settings.diagramTypes.editTitleManaged")
                            : t("settings.diagramTypes.editTitleOther")
                      }
                    >
                      {t("settings.common.edit")}
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function canEdit(c: CustomViewKindDTO, myEmail: string, myRole: "member" | "admin"): boolean {
  if (myRole === "admin") return true;
  if (c.centrallyManaged) return false;
  return c.creatorEmail === myEmail;
}

interface EditorProps {
  mode: "create" | "edit";
  workspaceId: string;
  initial?: CustomViewKindDTO;
  role?: "member" | "admin";
  onClose: () => void;
  onError: (msg: string | null) => void;
}

function Editor({ mode, workspaceId, initial, role, onClose, onError }: EditorProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [nameNl, setNameNl] = useState(initial?.name.nl ?? "");
  const [nameEn, setNameEn] = useState(initial?.name.en ?? "");
  const [descNl, setDescNl] = useState(initial?.description?.nl ?? "");
  const [descEn, setDescEn] = useState(initial?.description?.en ?? "");
  const [includedTypes, setIncludedTypes] = useState<Set<string>>(
    new Set(initial?.includedTypes ?? []),
  );
  // null = "alle matrix-toegestane relaties"; Set = subset
  const [includedRelationTypes, setIncludedRelationTypes] = useState<Set<string> | null>(
    initial?.includedRelationTypes ? new Set(initial.includedRelationTypes) : null,
  );
  const [centrallyManaged, setCentrallyManaged] = useState(initial?.centrallyManaged ?? false);
  const [busy, setBusy] = useState(false);

  // List element-types grouped per layer for the picker.
  const elementTypesByLayer = useMemo(() => {
    const reg = getRegistry();
    const types = reg.listActiveElementTypes();
    const byLayer = new Map<string, typeof types>();
    for (const t of types) {
      const arr = byLayer.get(t.layer) ?? [];
      arr.push(t);
      byLayer.set(t.layer, arr);
    }
    for (const arr of byLayer.values()) {
      arr.sort((a, b) =>
        resolveBilingual(a.label, lang).localeCompare(resolveBilingual(b.label, lang)),
      );
    }
    return Array.from(byLayer.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [lang]);

  const allRelationTypes = useMemo(() => {
    return getRegistry()
      .listActiveRelationTypes()
      .slice()
      .sort((a, b) =>
        resolveBilingual(a.label, lang).localeCompare(resolveBilingual(b.label, lang)),
      );
  }, [lang]);

  const toggleType = (id: string) =>
    setIncludedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleRelationType = (id: string) =>
    setIncludedRelationTypes((prev) => {
      const next = new Set(prev ?? allRelationTypes.map((r) => r.id));
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onSave = async () => {
    onError(null);
    if (!nameNl.trim() && !nameEn.trim()) {
      onError(t("settings.diagramTypes.validationNameRequired"));
      return;
    }
    if (includedTypes.size === 0) {
      onError(t("settings.diagramTypes.validationTypesRequired"));
      return;
    }
    setBusy(true);
    try {
      if (mode === "create") {
        await useWorkspaceData.getState().createCustomViewKind(workspaceId, {
          name_nl: nameNl.trim() || nameEn.trim(),
          name_en: nameEn.trim() || nameNl.trim(),
          description_nl: descNl.trim() || null,
          description_en: descEn.trim() || null,
          included_types: [...includedTypes],
          included_relation_types: includedRelationTypes ? [...includedRelationTypes] : null,
        });
      } else if (initial) {
        await useWorkspaceData.getState().updateCustomViewKind(workspaceId, initial.id, {
          name_nl: nameNl.trim() || initial.name.nl || initial.name.en || initial.id,
          name_en: nameEn.trim() || initial.name.en || initial.name.nl || initial.id,
          description_nl: descNl.trim() || null,
          description_en: descEn.trim() || null,
          included_types: [...includedTypes],
          included_relation_types: includedRelationTypes ? [...includedRelationTypes] : null,
          ...(role === "admin" ? { centrally_managed: centrallyManaged } : {}),
        });
      }
      onClose();
    } catch (err) {
      onError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const checkbox = "h-4 w-4 shrink-0 accent-(--ld-primary)";

  return (
    <div className="rounded-lg border border-(--ld-border) bg-(--ld-raised) p-4">
      <h3 className={ld.sectionTitle}>
        {mode === "create"
          ? t("settings.diagramTypes.editorTitleCreate")
          : t("settings.diagramTypes.editorTitleEdit")}
      </h3>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <TranslatableField
          label={t("settings.diagramTypes.nameNl")}
          lang="nl"
          kind="name"
          value={nameNl}
          counterpart={nameEn}
          onChange={setNameNl}
          onTranslated={setNameEn}
          disabled={busy}
        />
        <TranslatableField
          label={t("settings.diagramTypes.nameEn")}
          lang="en"
          kind="name"
          value={nameEn}
          counterpart={nameNl}
          onChange={setNameEn}
          onTranslated={setNameNl}
          disabled={busy}
        />
        <TranslatableField
          label={t("settings.diagramTypes.descriptionNl")}
          lang="nl"
          kind="description"
          value={descNl}
          counterpart={descEn}
          onChange={setDescNl}
          onTranslated={setDescEn}
          disabled={busy}
          multiline
        />
        <TranslatableField
          label={t("settings.diagramTypes.descriptionEn")}
          lang="en"
          kind="description"
          value={descEn}
          counterpart={descNl}
          onChange={setDescEn}
          onTranslated={setDescNl}
          disabled={busy}
          multiline
        />
      </div>

      <fieldset className="mt-5">
        <legend className={ld.label}>
          {t("settings.diagramTypes.allowedConceptTypes", { count: includedTypes.size })}
        </legend>
        <div className="mt-2 grid max-h-72 grid-cols-2 gap-x-4 gap-y-3 overflow-y-auto rounded-md border border-(--ld-border) bg-(--ld-surface) p-3 md:grid-cols-3">
          {elementTypesByLayer.map(([layer, types]) => (
            <div key={layer}>
              <p className="mb-1.5 font-medium text-(--ld-muted) text-xs capitalize">
                {layer.replace(/_/g, " ")}
              </p>
              <ul className="flex flex-col gap-1">
                {types.map((t) => (
                  <li key={t.id}>
                    <label className="flex cursor-pointer items-center gap-2 text-[13px]">
                      <input
                        type="checkbox"
                        className={checkbox}
                        checked={includedTypes.has(t.id)}
                        onChange={() => toggleType(t.id)}
                        disabled={busy}
                      />
                      <span>{resolveBilingual(t.label, lang)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </fieldset>

      <fieldset className="mt-5">
        <legend className={ld.label}>
          {t("settings.diagramTypes.allowedRelationTypes")}
          <span className="font-normal text-(--ld-muted)">
            {includedRelationTypes
              ? t("settings.diagramTypes.relationSubset", {
                  count: includedRelationTypes.size,
                })
              : t("settings.diagramTypes.relationAllAllowed")}
          </span>
        </legend>
        <label className="mt-2 flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            className={checkbox}
            checked={includedRelationTypes !== null}
            onChange={() =>
              setIncludedRelationTypes((prev) =>
                prev === null ? new Set(allRelationTypes.map((r) => r.id)) : null,
              )
            }
            disabled={busy}
          />
          <span>{t("settings.diagramTypes.restrictRelationTypes")}</span>
        </label>
        {includedRelationTypes && (
          <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-(--ld-border) bg-(--ld-surface) p-3 md:grid-cols-3">
            {allRelationTypes.map((r) => (
              <li key={r.id}>
                <label className="flex cursor-pointer items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    className={checkbox}
                    checked={includedRelationTypes.has(r.id)}
                    onChange={() => toggleRelationType(r.id)}
                    disabled={busy}
                  />
                  <span>{resolveBilingual(r.label, lang)}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </fieldset>

      {role === "admin" && mode === "edit" && (
        <label className="mt-5 flex items-start gap-2 text-[13px]">
          <input
            type="checkbox"
            className={`${checkbox} mt-0.5`}
            checked={centrallyManaged}
            onChange={(e) => setCentrallyManaged(e.target.checked)}
            disabled={busy}
          />
          <span>
            <span className="font-medium">{t("settings.diagramTypes.centrallyManagedLabel")}</span>
            <span className="text-(--ld-muted)">
              {t("settings.diagramTypes.centrallyManagedHint")}
            </span>
          </span>
        </label>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className={ld.secondary}>
          {t("settings.common.cancel")}
        </button>
        <button type="button" onClick={onSave} disabled={busy} className={ld.primary}>
          {mode === "create" ? t("settings.common.create") : t("settings.common.save")}
        </button>
      </div>
    </div>
  );
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? `HTTP ${err.status}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Editable input/textarea that calls `api.translate` on blur and mirrors
 * the result into the counterpart language. Same last-edit-wins behavior
 * as `BilingualField` in the attribute panel.
 *
 * - When the source value is empty on blur: noop.
 * - On API error / no Anthropic sign-in: silent fallback to copying the
 *   source verbatim, so the form stays usable without translation.
 * - Skips when the counterpart was edited concurrently (compare against
 *   the live value at translation-finish time, not the snapshot).
 */
function TranslatableField({
  label,
  lang,
  kind,
  value,
  counterpart,
  onChange,
  onTranslated,
  disabled,
  multiline,
}: {
  label: string;
  lang: "nl" | "en";
  kind: "name" | "description";
  value: string;
  counterpart: string;
  onChange: (v: string) => void;
  onTranslated: (v: string) => void;
  disabled?: boolean;
  multiline?: boolean;
}) {
  const { t } = useTranslation();
  const [translating, setTranslating] = useState(false);
  const targetLang: "nl" | "en" = lang === "nl" ? "en" : "nl";

  const onBlur = async () => {
    const text = value.trim();
    if (!text) return;
    const counterpartAtStart = counterpart;
    setTranslating(true);
    try {
      const res = await api.translate({
        source_lang: lang,
        target_lang: targetLang,
        text,
        kind,
      });
      // If the user typed in the OTHER field while the request was
      // in flight, don't clobber their work.
      if (counterpartAtStart !== counterpart) return;
      onTranslated(res.text);
    } catch {
      // No Anthropic sign-in (412) or other failure → copy source verbatim
      // as a sensible fallback. Same behaviour as the worker's meta-
      // response detector.
      if (counterpartAtStart === counterpart) onTranslated(text);
    } finally {
      setTranslating(false);
    }
  };

  const sharedProps = {
    className: ld.input,
    value,
    disabled,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(e.target.value),
    onBlur,
  };

  return (
    <label className="block">
      <span className="flex items-center justify-between">
        <span className={ld.label}>{label}</span>
        {translating && (
          <span role="status" className="text-(--ld-muted) text-xs">
            {t("settings.diagramTypes.translating")}
          </span>
        )}
      </span>
      {multiline ? (
        <textarea rows={2} {...sharedProps} className={`${ld.input} h-auto py-2`} />
      ) : (
        <input type="text" {...sharedProps} />
      )}
    </label>
  );
}

// Re-export so the parent SettingsPage doesn't need to import workspace store.
export function useDiagramTypesAutoload(): void {
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  useEffect(() => {
    if (!workspaceId || !branchId) return;
    // The workspace store hydrates this on editor load already, but the
    // user might land on /settings without ever opening the editor —
    // re-hydrate so the list reflects what's on the server.
    void useWorkspaceData.getState().hydrate(workspaceId, branchId);
  }, [workspaceId, branchId]);
}
